import React, { useState, useCallback, useEffect } from 'react';
import { Button, Table, Tabs, Typography, Tag, Space, Image, Statistic, Row, Col, Tooltip } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import useDocumentPageHook from '../DocumentPage/DocumentPageHook';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import CompanyDetailsPage from './CompanyDetailsPage';

const { Title } = Typography;
const { TabPane } = Tabs;

const TaskDetailPage = () => {
  const { getSystemTasks, systemTasks } = useDocumentPageHook();
  const navigate = useNavigate();
  const { sessionId } = useParams();
  const { state } = useLocation();
  const session = state?.session;

  const [selectedLead, setSelectedLead] = useState(null);
  const [detailsModalVisible, setDetailsModalVisible] = useState(false);

  const fetchSystemTasks = useCallback(async () => {
    const data = await getSystemTasks(sessionId);
    if (data) {
      console.log(data)
    } else {
      console.error('Failed to load session details.');
    }
  }, [sessionId]);

  useEffect(() => {
    fetchSystemTasks();
  }, []);

  if (!session) {
    return (
      <div>
        <Title level={2}>No session data available</Title>
        <Button onClick={() => navigate('/leads/tasks')}>Back</Button>
      </div>
    );
  }

  const renderSetupSnapshot = (snapshot) => {
    const sections = [
      { title: 'Campaign Setup', data: snapshot?.search_criteria_setup || {} },
      { title: 'Email Setup', data: snapshot?.email_setup || {} },
      { title: 'Company Setup', data: snapshot?.company_setup || {} },
    ];

    return sections.map(section => {
      const data = [];
      for (const [key, value] of Object.entries(section.data)) {
        data.push({ field: key, value: Array.isArray(value) ? value.join(', ') : value });
      }
      return { title: section.title, data };
    });
  };

  const formattedData = renderSetupSnapshot(session);

  const columns = [
    {
      title: 'Relevance',
      dataIndex: ['company_data', 'relevance'],
      key: 'relevance',
      render: (relevance, record) => {
        let color = relevance === 'RELEVANT' ? 'green' : 'red';
        return (
          <Tag color={color}>{relevance}</Tag>
        );
      },
    },
    {
      title: 'Company Name',
      key: 'company_name',
      render: (_, record) => (
        <Space>
          <Image
            width={40}
            height={40}
            src={record.company_data?.company_logo_url || '/person_icon.png'}
            style={{ borderRadius: '50%' }}
            alt="logo"
          />
          {record.company_data?.company_name}
        </Space>
      )
    },
    {
      title: 'Industry',
      dataIndex: ['company_data', 'company_industry'],
      key: 'industry',
      render: industry => (
        <Tag color="blue">{industry}</Tag>
      )
    },
    {
      title: 'Status',
      key: 'status',
      render: (_, record) => (
        <Tag>{record.status}</Tag>
      ),
    },
    {
      title: 'Action',
      key: 'action',
      render: (_, record) => (
        <Button
          icon={<InfoCircleOutlined />}
          onClick={() => {
            setSelectedLead(record);
            setDetailsModalVisible(true);
          }}
        >
          Details
        </Button>
      )
    }
  ];

  // Example statistics data, replace with actual calculation
  const statistics = systemTasks?.statistics || {}

  return (
    <div style={{ 
      width: '100%',
      margin: '0 auto', 
      padding: '22px 66px', 
      backgroundColor: '#fff', 
      borderRadius: '16px', 
      boxShadow: '0 4px 8px 0 rgba(0,0,0,0.2)',
      display: 'flex',
      flexDirection: 'column',
      minHeight: '0',
    }}>
      <div style={{ display: 'flex' }}>
        <Title level={2} style={{ marginTop: 0}}>Task: {session.name || ''}</Title>
      </div>
      <Tabs defaultActiveKey="1" style={{ width: '100%' }}>
        <TabPane tab={<span style={{ fontWeight: 'bold' }}>Overview</span>} key="1">
          <div style={{ marginBottom: 16 }}>
          <Row gutter={16}>
            <Col span={6}>
              <Statistic 
                title={<span>Processing <Tooltip title="Number of tasks currently being processed"><InfoCircleOutlined /></Tooltip></span>} 
                value={statistics.Processing} 
              />
            </Col>
            <Col span={6}>
              <Statistic 
                title={<span>Complete <Tooltip title="Number of tasks that are completed"><InfoCircleOutlined /></Tooltip></span>} 
                value={statistics.Complete + statistics["Reached out"]} 
              />
            </Col>
            <Col span={6}>
              <Statistic 
                title={<span>Error <Tooltip title="Number of tasks that have failed due to error"><InfoCircleOutlined /></Tooltip></span>} 
                value={statistics.Error} 
              />
            </Col>
            <Col span={6}>
              <Statistic 
                title={<span>Missing Contact <Tooltip title="Number of tasks missing contact information"><InfoCircleOutlined /></Tooltip></span>} 
                value={statistics["Missing Contact"]} 
              />
            </Col>
          </Row>

          </div>
          {formattedData.map(section => (
            <div key={section.title}>
              <Title level={4}>{section.title}</Title>
              <Table
                columns={[
                  { title: '', dataIndex: 'field', key: 'field' },
                  { title: '', dataIndex: 'value', key: 'value' }
                ]}
                dataSource={section.data}
                pagination={false}
                rowKey="field"
                showHeader={false}
              />
            </div>
          ))}
        </TabPane>
        <TabPane tab={<span style={{ fontWeight: 'bold' }}>Companies</span>} key="2">
          <Table
            columns={columns}
            dataSource={systemTasks?.company_details}
            rowKey="url_id"
            onRow={(record) => ({
              onClick: () => {
                setSelectedLead(record);
              }
            })}
          />
        </TabPane>
      </Tabs>

      <CompanyDetailsPage
        visible={detailsModalVisible}
        onClose={() => setDetailsModalVisible(false)}
        record={selectedLead}
        showEmail={true}
      />
    </div>
  );
};

export default TaskDetailPage;
